import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import type { DashboardScopeInfo } from '@/types/api';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-[250px] items-center justify-center text-sm text-gray-500">
      {message}
    </div>
  );
}

// Explicit currency + locale (from the restaurant/group scope, not the viewer's browser)
// so the same figure renders identically for every user and reflects the actual currency
// restaurants transact in, instead of an assumed "$".
function formatMoney(value: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value);
}

// Matches the .card look (rounded-lg border-gray-200 bg-white) instead of Recharts'
// unstyled white-on-white default tooltip.
const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  },
  labelStyle: { color: '#111827', fontWeight: 600 },
};

const PIE_LABEL_MAX_LENGTH = 12;

function truncateLabel(text: string): string {
  return text.length > PIE_LABEL_MAX_LENGTH
    ? `${text.slice(0, PIE_LABEL_MAX_LENGTH - 1)}…`
    : text;
}

function scopeBannerText(scope: DashboardScopeInfo): string {
  switch (scope.type) {
    case 'EMPTY':
      return 'You do not have access to any restaurants yet — ask an administrator to grant you a role.';
    case 'GROUP':
      return `Showing data for ${scope.groupNames.join(', ')} (${scope.restaurantCount} restaurant${scope.restaurantCount === 1 ? '' : 's'}).`;
    case 'RESTAURANT':
      return `Showing data for ${scope.restaurantNames.join(', ')}.`;
    case 'MIXED': {
      const extras = scope.restaurantCount - scope.groupNames.length;
      return `Showing data for ${scope.groupNames.join(', ')} and ${extras} additional restaurant${extras === 1 ? '' : 's'}.`;
    }
    case 'PLATFORM':
      // PLATFORM scope hides the banner via the JSX guard; no text needed.
      return '';
    default: {
      // Compile-time exhaustiveness check — if a new DashboardScopeInfo['type']
      // literal is added without a matching case here, this line will fail to
      // compile (`Type 'string' is not assignable to type 'never'`).
      const _exhaustive: never = scope.type;
      console.warn('Unhandled scope type', _exhaustive);
      return 'Showing scoped data.';
    }
  }
}

export default function DashboardPage() {
  const {
    data: stats,
    isLoading,
    isError,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => apiClient.getDashboardStats(),
  });

  // Scope is the user's session-level membership view, not a per-stats-fetch concern.
  // Reading from authStore lets the badge/banner render even before /dashboard/stats resolves.
  const scope = useAuthStore((s) => s.scope);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load dashboard data</div>
        <button onClick={() => refetch()} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  const revenueData = stats?.revenueByHour ?? [];
  const tableStatusData = stats?.tableStatusDistribution ?? [];
  const topMenuItems = stats?.topMenuItems ?? [];
  const recentActivity = stats?.recentActivity ?? [];
  const currency = stats?.currency ?? 'USD';
  const locale = stats?.locale ?? 'en-US';

  const totalRestaurants = stats?.totalRestaurants;
  const activeRestaurants = stats?.activeRestaurants ?? 0;
  const restaurantsCard =
    totalRestaurants == null
      ? {
          title: 'Restaurants',
          value: activeRestaurants as number | string,
          subtitle: scope
            ? `${scope.restaurantCount} in scope`
            : '',
          icon: '🏪',
          color: 'bg-blue-500',
        }
      : {
          title: 'Total Restaurants',
          value: totalRestaurants as number | string,
          subtitle: `${activeRestaurants} active`,
          icon: '🏪',
          color: 'bg-blue-500',
        };

  const onlineTerminals = stats?.onlineTerminals;
  const totalTerminals = stats?.totalTerminals;
  const terminalsCard =
    onlineTerminals == null
      ? {
          title: 'Online Terminals',
          value: '—' as number | string,
          subtitle: 'Not yet available for scoped users',
          icon: '💻',
          color: 'bg-green-500',
        }
      : {
          title: 'Online Terminals',
          value: onlineTerminals as number | string,
          subtitle: `of ${totalTerminals ?? 0} total`,
          icon: '💻',
          color: 'bg-green-500',
        };

  const cards = [
    restaurantsCard,
    terminalsCard,
    {
      title: 'Tables Occupied',
      value: (stats?.occupiedTables ?? 0) as number | string,
      subtitle: `of ${stats?.totalTables ?? 0} total`,
      icon: '🪑',
      color: 'bg-yellow-500',
    },
    {
      title: "Today's Revenue",
      value: formatMoney(stats?.todayRevenue ?? 0, currency, locale) as number | string,
      subtitle:
        (stats?.revenueChange ?? 0) === 0
          ? 'No change from yesterday'
          : `${(stats?.revenueChange ?? 0) > 0 ? '+' : ''}${(stats?.revenueChange ?? 0).toFixed(1)}% from yesterday`,
      icon: '💰',
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">
              Welcome back! Here's what's happening today.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-gray-400">
              {isFetching ? 'Refreshing…' : `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}`}
            </span>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="btn btn-outline h-9 disabled:opacity-50"
              title="Refresh dashboard data"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        {stats?.demoMode && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
            <span>&#9888;&#65039;</span>
            <span>No POS data has been synced yet — once your terminals start syncing, real figures will appear here.</span>
          </div>
        )}
        {scope && scope.type !== 'PLATFORM' && (
          <div className="mt-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-2.5 text-sm text-blue-900">
            {scopeBannerText(scope)}
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => (
          <div key={card.title} className="card p-6">
            <div className="flex items-center">
              <div className={`flex-shrink-0 rounded-lg ${card.color} p-3`}>
                <span className="text-2xl">{card.icon}</span>
              </div>
              <div className="ml-4 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  {card.title}
                </dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">
                  {card.value}
                </dd>
                <dd className="mt-1 text-xs text-gray-500">{card.subtitle}</dd>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Today's Revenue Trend
          </h3>
          {revenueData.length === 0 ? (
            <EmptyState message="No revenue data for today yet." />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip {...chartTooltipStyle} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name={`Revenue (${currency})`}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Orders Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Orders Per Hour
          </h3>
          {revenueData.length === 0 ? (
            <EmptyState message="No orders recorded yet today." />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip {...chartTooltipStyle} />
                <Legend />
                <Bar dataKey="orders" fill="#10b981" name="Orders" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Table Status Pie Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Table Status Distribution
          </h3>
          {tableStatusData.length === 0 ? (
            <EmptyState message="No table data available." />
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={tableStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${truncateLabel(name)} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {tableStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip {...chartTooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Top Menu Items */}
        <div className="card p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Top Menu Items Today
          </h3>
          {topMenuItems.length === 0 ? (
            <EmptyState message="No items sold today yet." />
          ) : (
            <div className="space-y-3">
              {topMenuItems.map((item, index) => (
                <div
                  key={item.name}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-semibold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{item.name}</div>
                      <div className="text-sm text-gray-500">
                        {item.orders} orders
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {formatMoney(item.revenue, currency, locale)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatMoney(item.orders > 0 ? item.revenue / item.orders : 0, currency, locale)} avg
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity — only render when there is real activity to show */}
      {recentActivity.length > 0 && (
        <div className="card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center space-x-4">
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-lg ${activity.color} flex items-center justify-center text-xl`}
                  >
                    {activity.icon}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.text}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
